import React from 'react';
import { Filter, RotateCcw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PRESETS, presetRange } from './posUtils';

const TYPES = [['all', 'All'], ['new', 'New'], ['repeat', 'Repeat'], ['inactive', 'Inactive'], ['lost', 'Lost']];

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export const PosFilters = ({ draft, setDraft, latest, onApply, onReset, compare, setCompare }) => {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const onPreset = (p) => {
    const r = presetRange(p, latest);
    setDraft((d) => ({ ...d, preset: p, ...(r ? { from_month: r[0], to_month: r[1] } : {}) }));
  };
  const onMonth = (k, v) => setDraft((d) => ({ ...d, preset: 'custom', [k]: v }));
  return (
    <Card className="p-4 mb-4" data-testid="pos-filters">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="Period">
          <Select value={draft.preset} onValueChange={onPreset}>
            <SelectTrigger className="h-9" data-testid="filter-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESETS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`preset-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="From Month">
          <Input type="month" className="h-9" value={draft.from_month} onChange={(e) => onMonth('from_month', e.target.value)} data-testid="filter-from-month" />
        </Field>
        <Field label="To Month">
          <Input type="month" className="h-9" value={draft.to_month} onChange={(e) => onMonth('to_month', e.target.value)} data-testid="filter-to-month" />
        </Field>
        <Field label="Customer Type">
          <Select value={draft.ctype} onValueChange={(v) => set('ctype', v)}>
            <SelectTrigger className="h-9" data-testid="filter-ctype"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`ctype-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Customer ID / Name / Mobile">
          <Input className="h-9" value={draft.q} onChange={(e) => set('q', e.target.value)} placeholder="Search" data-testid="filter-search" />
        </Field>
        <Field label="Sales Min – Max (₹)">
          <div className="flex gap-1">
            <Input type="number" className="h-9" value={draft.min_sales} onChange={(e) => set('min_sales', e.target.value)} placeholder="Min" data-testid="filter-min-sales" />
            <Input type="number" className="h-9" value={draft.max_sales} onChange={(e) => set('max_sales', e.target.value)} placeholder="Max" data-testid="filter-max-sales" />
          </div>
        </Field>
        <Field label="Min Orders">
          <Input type="number" className="h-9" value={draft.min_orders} onChange={(e) => set('min_orders', e.target.value)} data-testid="filter-min-orders" />
        </Field>
        <Field label="Min Points Earned / Burned">
          <div className="flex gap-1">
            <Input type="number" className="h-9" value={draft.min_points_earned} onChange={(e) => set('min_points_earned', e.target.value)} placeholder="Earned" data-testid="filter-min-pe" />
            <Input type="number" className="h-9" value={draft.min_points_burned} onChange={(e) => set('min_points_burned', e.target.value)} placeholder="Burned" data-testid="filter-min-pb" />
          </div>
        </Field>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={compare} onCheckedChange={setCompare} data-testid="filter-compare-toggle" />
          Compare with previous period
        </label>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onReset} data-testid="filter-reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={onApply} data-testid="filter-apply-btn"><Filter className="w-4 h-4 mr-1" /> Apply Filters</Button>
        </div>
      </div>
    </Card>
  );
};
